import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Plus, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AdminCard, AdminPageHeader } from "@/components/admin/admin-shell";
import { adminInsert, adminList, adminUpdate } from "@/lib/admin";
import { azError } from "@/lib/az-errors";

export const Route = createFileRoute("/_authenticated/admin/ayarlar")({
  component: Page,
});

const labels: Record<string, string> = {
  site_name: "Sayt adı",
  support_whatsapp: "Dəstək WhatsApp nömrəsi",
  support_email: "Dəstək e-poçtu",
  vip_price: "VIP paket qiyməti (AZN)",
  premium_price: "Premium paket qiyməti (AZN)",
  free_listing_limit: "Pulsuz elan limiti",
  maintenance_mode: "Texniki fasilə rejimi (on/off)",
};

function Page() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["admin", "site_settings"],
    queryFn: () => adminList("site_settings", "key", true),
  });
  const [values, setValues] = useState<Record<string, string>>({});
  const [newKey, setNewKey] = useState("");

  useEffect(() => {
    if (!query.data) return;
    const next: Record<string, string> = {};
    for (const row of query.data) next[String(row["key"])] = String(row["value"] ?? "");
    setValues(next);
  }, [query.data]);

  const save = useMutation({
    mutationFn: async () => {
      for (const [key, value] of Object.entries(values)) {
        await adminUpdate("site_settings", key, { value, updated_at: new Date().toISOString() }, "key");
      }
    },
    onSuccess: () => {
      toast.success("Ayarlar yadda saxlanıldı");
      qc.invalidateQueries({ queryKey: ["admin", "site_settings"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  const addKey = useMutation({
    mutationFn: (key: string) => adminInsert("site_settings", { key, value: "" }),
    onSuccess: () => {
      setNewKey("");
      toast.success("Yeni ayar əlavə edildi");
      qc.invalidateQueries({ queryKey: ["admin", "site_settings"] });
    },
    onError: (e) => toast.error(azError(e)),
  });

  return (
    <div>
      <AdminPageHeader title="Sayt ayarları" description="Qiymətlər, əlaqə məlumatları və limitlər" />

      <AdminCard className="p-5">
        {query.isLoading ? (
          <Loader2 className="mx-auto size-5 animate-spin text-primary" />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {Object.keys(values).map((key) => (
              <div key={key}>
                <Label className="mb-1.5 block text-xs font-semibold">{labels[key] ?? key}</Label>
                <Input
                  className="h-11 rounded-full"
                  value={values[key] ?? ""}
                  onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        )}
        <div className="mt-6 flex flex-wrap gap-2">
          <Button className="rounded-full" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}{" "}
            Yadda saxla
          </Button>
        </div>
      </AdminCard>

      <AdminCard className="mt-5 p-5">
        <h2 className="mb-3 text-sm font-bold">Yeni ayar açarı əlavə et</h2>
        <div className="flex flex-wrap gap-2">
          <Input
            className="h-11 w-64 rounded-full"
            value={newKey}
            placeholder="məsələn: facebook_link"
            onChange={(e) => setNewKey(e.target.value)}
          />
          <Button
            variant="outline"
            className="rounded-full"
            disabled={!newKey.trim() || addKey.isPending}
            onClick={() => addKey.mutate(newKey.trim())}
          >
            <Plus className="size-4" /> Əlavə et
          </Button>
        </div>
      </AdminCard>
    </div>
  );
}
